import { useCallback, useEffect, useState } from 'react';
import { salesApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ THE ONE SCREEN ═══
 *
 * ⚠ EXACTLY ONE HERO FIGURE. More than one and none of them is the answer.
 *
 * ⚠ EVERY TILE IS A COUNT OF A LIST THAT EXISTS, and every list has a screen. A figure with no list
 * behind it is decoration with a decimal point.
 *
 * ⚠ NO TIME SERIES YET, ON PURPOSE. Two accounts and eleven deals do not make a trend; a line
 * through four points is a shape the eye reads as meaning and the data cannot support. It goes in
 * when there are twelve months of invoices to draw it from.
 */

interface Tile { label: string; value: string; sub: string | null; tone: string }
interface Band { label: string; count: number; stages: string[] }
interface View {
  headline: string; heroValue: string; heroLabel: string;
  tiles: Tile[]; bands: Band[]; openTotal: number;
}

/*
 * ⚠ A SINGLE-HUE ORDINAL RAMP, VALIDATED RATHER THAN CHOSEN BY EYE. Light to dark across the
 * pipeline, each step visibly apart from its neighbour and the lightest one still clear of the
 * surface. Dark mode gets its OWN steps from the same ramp — not an inverted copy, which fails the
 * same check every time.
 *
 * ⚠ AND THIS IS WHY THERE ARE FIVE BANDS AND NOT SIX. Six steps could not all stay visibly apart
 * while keeping the lightest off the surface, so the honest fix was fewer bands rather than a
 * second hue — the two demo stages are one phase of one conversation anyway.
 */
const RAMP_LIGHT = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#0d366b'];
const RAMP_DARK = ['#184f95', '#256abf', '#3987e5', '#6da7ec', '#9ec5f4'];

/**
 * Which ramp, decided by THE SURFACE THAT IS ACTUALLY PAINTED.
 *
 * ⚠ NOT prefers-color-scheme. This app has a theme toggle that writes data-theme on <html> and
 * remembers the choice, so a person whose phone is in dark mode but who chose the light theme is
 * looking at a LIGHT surface. Asking the operating system would have put the dark ramp on it, and
 * the dark ramp's lightest step sits at 1.74:1 against a light surface — the exact contrast failure
 * the validator refuses. The attribute is the only thing that always agrees with what is on screen.
 *
 * ⚠ Read from the attribute rather than useTheme() on purpose: this panel is also rendered in the
 * offline preview harness, which has no ThemeProvider, and useTheme() throws without one.
 */
function useSurfaceTheme(): boolean {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    const read = () => setDark(document.documentElement.getAttribute('data-theme') === 'dark');
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/** ⚠ Reserved status steps — never reused as a series colour, and always with words beside them. */
const TONE: Record<string, string> = {
  good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b',
  neutral: 'var(--ink-3)',
};

export function SalesDashboard({ onGo }: { onGo: (view: string) => void }) {
  const [v, setV] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dark = useSurfaceTheme();

  const load = useCallback(async () => {
    try {
      const d = await salesApi.get<View>('/api/platform/sales/dashboard');
      setV(d && Array.isArray(d.tiles) ? d : null);
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not load the dashboard.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!v) {
    return <div className="card" style={{ padding: 24, color: 'var(--ink-3)' }}>{error ?? 'Loading…'}</div>;
  }

  const ramp = dark ? RAMP_DARK : RAMP_LIGHT;
  const total = Math.max(1, v.openTotal);

  /* ⚠ THE ROUNDED ENDS BELONG TO THE BAR, NOT TO THE ARRAY. An empty stage draws nothing, so if the
     last band is empty the rightmost SEGMENT is not the last index — rounding by index left the bar
     with one soft end and one square one, which reads as a bar that was cut off. First and last
     drawn, not first and last defined. */
  const drawn = v.bands.map((_, i) => i).filter((i) => v.bands[i].count > 0);
  const firstDrawn = drawn[0];
  const lastDrawn = drawn[drawn.length - 1];

  return (
    <div>
      <div className="card" style={{ padding: '26px 24px', marginBottom: 16 }}>
        {/* ⚠ The hero is the money still in play, and it is the only figure at this size. */}
        <div style={{ fontSize: 52, fontWeight: 650, lineHeight: 1.05, letterSpacing: '-0.02em' }}>
          {v.heroValue}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>{v.heroLabel}</div>
        {/* ⚠ The most urgent TRUE thing, not an average of everything. */}
        <p style={{ fontSize: 14.5, color: 'var(--ink)', margin: '16px 0 0', maxWidth: 620, lineHeight: 1.5 }}>
          {v.headline}
        </p>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))',
        gap: 10, marginBottom: 16,
      }}>
        {v.tiles.map((t) => (
          <div key={t.label} className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{t.label}</div>
            {/* ⚠ Proportional figures, not tabular — tabular gives every digit the width of a zero
                and a number like 121 looks loose at this size. Tabular is for columns. */}
            <div style={{ fontSize: 26, fontWeight: 650, lineHeight: 1.2, marginTop: 2 }}>{t.value}</div>
            {t.sub && (
              <div style={{ fontSize: 11.5, marginTop: 2, display: 'flex', gap: 5, alignItems: 'center' }}>
                {/* ⚠ A dot beside the words, never coloured text. A tile that means something only
                    to somebody who can see red means nothing. */}
                {t.tone !== 'neutral' && (
                  <span aria-hidden="true" style={{
                    width: 7, height: 7, borderRadius: 999, background: TONE[t.tone] ?? TONE.neutral,
                    flex: '0 0 auto',
                  }} />
                )}
                <span style={{ color: 'var(--ink-3)' }}>{t.sub}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
          <span style={{ fontWeight: 650, fontSize: 13 }}>Where the {v.openTotal} open deals sit</span>
          <button className="btn" style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px' }}
                  onClick={() => onGo('pipeline')}>Open the pipeline</button>
        </div>

        {/* ⚠ 2px GAPS IN THE SURFACE COLOUR separate the segments — not borders. A stroke round each
            band adds ink that is not data, and neighbouring steps read apart because of the gap. */}
        <div style={{ display: 'flex', gap: 2, height: 22, marginBottom: 10 }}>
          {v.bands.map((b, i) => b.count > 0 && (
            <div key={b.label}
                 title={`${b.label}: ${b.count}`}
                 style={{
                   flex: `${b.count} 0 0`,
                   background: ramp[i] ?? ramp[ramp.length - 1],
                   // 4px rounded data-ends, square where the bands meet.
                   borderRadius: firstDrawn === lastDrawn ? 4
                     : i === firstDrawn ? '4px 0 0 4px'
                     : i === lastDrawn ? '0 4px 4px 0' : 0,
                   minWidth: 6,
                 }} />
          ))}
        </div>

        {/* ⚠ A legend is always present for two or more series — identity is never colour alone.
            ⚠ AND EVERY STAGE STAYS IN IT, INCLUDING THE EMPTY ONES. "Negotiating 0" is a fact worth
            reading; dropping it would leave no way to tell an empty stage from one that does not
            exist. But its swatch is HOLLOW, because a filled swatch promises a segment in the bar
            and there is no segment to find. */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {v.bands.map((b, i) => (
            <span key={b.label} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
              <span aria-hidden="true" style={{
                width: 9, height: 9, borderRadius: 2,
                background: b.count > 0 ? (ramp[i] ?? ramp[ramp.length - 1]) : 'transparent',
                boxShadow: b.count > 0 ? undefined : `inset 0 0 0 1.5px ${ramp[i] ?? ramp[ramp.length - 1]}`,
                flex: '0 0 auto',
              }} />
              <span style={{ color: 'var(--ink-2)' }}>{b.label}</span>
              <span style={{ color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>{b.count}</span>
              <span style={{ color: 'var(--ink-3)' }}>
                {Math.round((b.count / total) * 100)}%
              </span>
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* ⚠ Every tile above corresponds to one of these. A number you cannot click through to a
            list is a number nobody acts on. */}
        <button className="btn" onClick={() => onGo('pipeline')}>Pipeline</button>
        <button className="btn" onClick={() => onGo('accounts')}>Accounts</button>
        <button className="btn" onClick={() => onGo('map')}>The market</button>
        <button className="btn" onClick={() => onGo('intel')}>Competition</button>
      </div>
    </div>
  );
}
